import cn from 'classnames';
import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { v4 as uuid } from 'uuid';

/**
 * Shared id for the single, app-level tooltip mounted via `GlobalTooltip`.
 * Anchor any element to it with `data-tooltip-id={GLOBAL_TOOLTIP_ID}` plus
 * `data-tooltip-content`. Because the tooltip is portaled to `document.body`,
 * it escapes table overflow clipping and parent stacking contexts.
 */
export const GLOBAL_TOOLTIP_ID = 'global-tooltip';

/**
 * Single tooltip instance for the whole app. Mount once near the root. Trigger
 * it from anywhere using the `data-tooltip-*` attributes referencing
 * `GLOBAL_TOOLTIP_ID`.
 */
export function GlobalTooltip() {
  return createPortal(
    <ReactTooltip
      className="!z-[9999] flex flex-col !rounded-[6px] !bg-gray-800 !text-center !text-12/120% !font-medium !text-white !opacity-100 !duration-200"
      id={GLOBAL_TOOLTIP_ID}
      offset={20}
      positionStrategy="fixed"
    />,
    document.body,
  );
}

type TooltipProps = {
  ['data-tooltip-id']: string;
};

export interface Tooltip extends Pick<
  React.ComponentProps<typeof ReactTooltip>,
  | 'className'
  | 'float'
  | 'id'
  | 'isOpen'
  | 'offset'
  | 'place'
  | 'positionStrategy'
> {
  children: React.ReactNode | ((props: TooltipProps) => React.ReactNode);
  clickable?: boolean;
  delayHide?: number;
  delayShow?: number;
  globalCloseEvents?: React.ComponentProps<
    typeof ReactTooltip
  >['globalCloseEvents'];
  hidden?: boolean;
  hint?: React.ReactNode;
  title?: React.ReactNode;
}

export function Tooltip({
  children,
  className,
  clickable = false,
  delayHide = 100,
  delayShow = 100,
  float,
  globalCloseEvents,
  hidden,
  hint,
  isOpen,
  offset,
  place = 'bottom',
  positionStrategy,
  title,
}: Tooltip) {
  const props = useMemo<TooltipProps>(
    () => ({
      'data-tooltip-id': uuid(),
    }),
    [],
  );

  return (
    <>
      {typeof children === 'function' ? (
        children(props)
      ) : (
        <span {...props}>{children}</span>
      )}
      {!hidden && (
        <ReactTooltip
          className={cn(
            '!z-10 flex flex-col !rounded-[6px] !bg-gray-800 !duration-200',
            clickable ? '!max-h-[90vh] !overflow-hidden' : '!overflow-visible',
            {
              // `className` can be used to override default max-w
              'max-w-[160px]': !className,
            },
            className,
          )}
          clickable={clickable}
          delayHide={delayHide}
          delayShow={delayShow}
          float={float}
          globalCloseEvents={globalCloseEvents}
          id={props['data-tooltip-id']}
          isOpen={isOpen}
          offset={offset}
          opacity={1}
          place={place}
          positionStrategy={positionStrategy}
        >
          <div className="flex flex-col gap-y-1">
            {title && (
              <span className="text-center text-12/120% font-medium text-white">
                {title}
              </span>
            )}
            {hint && (
              <span className="text-center text-12/120% font-normal text-gray-400">
                {hint}
              </span>
            )}
          </div>
        </ReactTooltip>
      )}
    </>
  );
}
